import React, { useEffect, useRef, useState } from "react";
import { FileDown } from "lucide-react";
import { AppButton } from "../ui";
import apiClient from "../../utils/apiClient";
import PdfView from "../Common/PdfView";
import { downloadPdf } from "../Fees/invoice/pdfActions";

// Long enough that typing a word renders once, short enough to feel live.
const DEBOUNCE_MS = 450;

/** The server's sentence from an arraybuffer error body, or a fallback. */
function messageOf(error) {
  try {
    const text = new TextDecoder().decode(error?.response?.data);
    return JSON.parse(text).message || "";
  } catch {
    return "";
  }
}

/**
 * PdfLivePreview — the REAL PDF for a settings form as it stands, rendered by
 * the server from the unsaved form on every change (nothing saved) through the
 * same renderer the real documents use, so a preview cannot differ from what
 * people receive. "Download this sample" saves the very bytes on screen.
 *
 * Props:
 *   url         the POST that answers with the PDF
 *   body        the form exactly as Save would send it
 *   refreshKey  changes when something outside the form changes the PDF
 *   heading     the small line above the page
 *   label       the document's accessible name
 *   fileName    the sample's download name
 *   footnote    the line beside the download
 *   toolbar     optional controls beside the heading
 */
export default function PdfLivePreview({ url, body, refreshKey, heading, label, fileName, footnote, toolbar = null }) {
  const [pdf, setPdf] = useState({ bytes: null, error: "", updating: true });
  const [attempt, setAttempt] = useState(0);
  const shownOnce = useRef(false);
  const request = JSON.stringify(body);

  useEffect(() => {
    const controller = new AbortController();
    setPdf((p) => ({ ...p, updating: true }));
    const timer = setTimeout(async () => {
      try {
        const { data } = await apiClient.post(url, JSON.parse(request), {
          responseType: "arraybuffer",
          signal: controller.signal,
        });
        shownOnce.current = true;
        setPdf({ bytes: new Uint8Array(data), error: "", updating: false });
      } catch (e) {
        if (controller.signal.aborted) return;
        setPdf((p) => ({ ...p, error: messageOf(e) || "The preview could not be drawn.", updating: false }));
      }
    }, shownOnce.current ? DEBOUNCE_MS : 0);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [url, request, attempt, refreshKey]);

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-muted">{heading}</span>
        {toolbar}
      </div>

      <div className="tw-rounded-lg tw-border tw-border-solid tw-border-line tw-bg-surface-sunken tw-p-3">
        <PdfView
          bytes={pdf.bytes}
          label={label}
          updating={pdf.updating && Boolean(pdf.bytes)}
          error={pdf.error}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      </div>

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <AppButton
          variant="outline"
          size="sm"
          onClick={() => downloadPdf(pdf.bytes, fileName)}
          disabled={!pdf.bytes || pdf.updating}
        >
          <FileDown size={14} /> Download this sample
        </AppButton>
        {footnote && <span className="tw-text-xs tw-text-content-muted">{footnote}</span>}
      </div>
    </div>
  );
}
